import { useEffect, useState } from "react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import StatusBadge from "components/admin/StatusBadge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Announcement,
  listAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
} from "services/admin/adminComunicacao.service";

const CATEGORY_LABEL: Record<string, string> = {
  atualizacao: "Atualização",
  manutencao: "Manutenção",
  incidente: "Incidente",
  novidade: "Novidade",
};

const emptyForm = {
  title: "",
  body: "",
  category: "atualizacao",
  audience: "todos",
  showBanner: true,
  showOnStatusPage: false,
};

const AdminComunicacao = () => {
  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    listAnnouncements({ limit: 50 })
      .then((resp) => setItems(resp.data))
      .catch((err) => console.error("[AdminComunicacao] erro:", err))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      await createAnnouncement(form as any);
      setForm(emptyForm);
      load();
    } catch (err) {
      console.error("[AdminComunicacao] erro ao criar:", err);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item: Announcement) => {
    await updateAnnouncement(item.id, { active: !item.active });
    load();
  };

  const handleDelete = async (id: number) => {
    await deleteAnnouncement(id);
    load();
  };

  return (
    <AdminLayout title="Central de Comunicação" subtitle="Comunicados internos e para clientes">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 flex flex-col gap-4 xl:col-span-1">
          <h3 className="text-[13px] font-semibold text-slate-700">Novo comunicado</h3>
          <div className="flex flex-col gap-1.5">
            <Label>Título</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Mensagem</Label>
            <textarea
              className="min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Categoria</Label>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {Object.entries(CATEGORY_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Destinatários</Label>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
            >
              <option value="todos">Todos</option>
              <option value="shopify">Clientes Shopify</option>
              <option value="nuvemshop">Clientes Nuvemshop</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.showBanner} onChange={(e) => setForm({ ...form, showBanner: e.target.checked })} />
            Exibir banner interno
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.showOnStatusPage} onChange={(e) => setForm({ ...form, showOnStatusPage: e.target.checked })} />
            Exibir na página de status
          </label>
          <Button type="submit" disabled={saving} className="bg-brand-sidebar hover:bg-brand-sidebar/90">
            {saving ? "Publicando..." : "Publicar comunicado"}
          </Button>
        </form>

        <div className="xl:col-span-2 flex flex-col gap-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : items.length ? (
            items.map((item) => (
              <AdminCard key={item.id}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm text-slate-800">{item.title}</h4>
                    <StatusBadge label={CATEGORY_LABEL[item.category]} tone="blue" />
                    <StatusBadge label={item.active ? "Ativo" : "Inativo"} tone={item.active ? "green" : "slate"} />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => toggleActive(item)}>
                      {item.active ? "Desativar" : "Ativar"}
                    </Button>
                    <Button variant="outline" onClick={() => handleDelete(item.id)}>Excluir</Button>
                  </div>
                </div>
                <p className="text-sm text-slate-600">{item.body}</p>
              </AdminCard>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum comunicado publicado.</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminComunicacao;
